import { useState, useCallback } from 'react';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getMembersApi } from '@/services/bolApiClient';
import { Member, PaginatedMembers } from '@/api/bol';

export interface UseMembersManagementReturn {
  members: Member[];
  loading: boolean;
  selectedMember: Member | null;
  setSelectedMember: (member: Member | null) => void;
  fetchMembers: (page?: number, pageSize?: number) => Promise<void>;
  createMember: (memberData: { identifier: string }) => Promise<void>;
  deleteMember: (identifier: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useMembersManagement = (): UseMembersManagementReturn => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [total, setTotal] = useState(0);
  
  const { showSuccess, showError } = useSnackbarNotification();

  const fetchMembers = useCallback(async (page: number = 0, pageSize: number = 10) => {
    console.log('Fetching members with:', { page, pageSize });

    setLoading(true);
    
    try {
      const api = getMembersApi();
      
      const response: PaginatedMembers = await api.listMembers({
        limit: pageSize,
        offset: page * pageSize
      });
      
      console.log('Members API response:', response);
      
      setMembers(response.results || []);
      setTotal(response.pagination?.total || 0);
    } catch (err: unknown) {
      console.error('Error fetching members:', err);
      const errorMessage = err instanceof Error
        ? err.message || 'Failed to fetch members'
        : 'Failed to fetch members';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createMember = useCallback(async (memberData: { identifier: string }) => {
    try {
      console.log('Creating member with data:', memberData);
      
      const api = getMembersApi();
      
      await api.createMember({
        contentType: 'application/json',
        member: memberData,
        source: true
      });
      
      showSuccess('Member created successfully');
    } catch (err: unknown) {
      console.error('Error creating member:', err);
      const errorMessage = err instanceof Error
        ? err.message || 'Failed to create member'
        : 'Failed to create member';
      showError(`Create member failed: ${errorMessage}`);
      throw err; // Re-throw to let the calling component handle form state
    }
  }, [showSuccess, showError]);

  const deleteMember = useCallback(async (identifier: string) => {
    try {
      const api = getMembersApi();
      
      await api.deleteMemberById({
        memberId: identifier
      });
      
      showSuccess('Member deleted successfully');
    } catch (err: unknown) {
      console.error('Error deleting member:', err);
      const errorMessage = err instanceof Error
        ? err.message || 'Failed to delete member'
        : 'Failed to delete member';
      showError(`Delete member failed: ${errorMessage}`);
      throw err; // Re-throw to let the calling component handle dialog state
    }
  }, [showSuccess, showError]);

  return {
    members,
    loading,
    selectedMember,
    setSelectedMember,
    fetchMembers,
    createMember,
    deleteMember,
    setTotal,
    total
  };
};